import { Nullable } from 'src/core/common/domain/types/types';
import { QueueElement, QueueElementType } from 'src/core/queue/domain/queue-element';

export type PresenceLogoutTrigger = 'timeout' | 'warning-modal-button';

/**
 * What presence control throws away when it logs a user out with changes still queued. It carries no task
 * content on purpose: it only has to tell how often this happens and which operations were lost.
 */
export class LostChangesReport {
  private static readonly LOGOUT_DESCRIPTION_BY_TRIGGER: Record<PresenceLogoutTrigger, string> = {
    timeout: 'logout done automatically',
    'warning-modal-button': 'user confirmed logout on the dialog shown',
  };

  private static readonly OPERATION_BY_TYPE: Record<QueueElementType, string> = {
    [QueueElementType.START]: 'Task start',
    [QueueElementType.STOP]: 'Task stop',
    [QueueElementType.CLOSE]: 'Task close',
    [QueueElementType.CONFIRM_CLOSE]: 'Task close confirmation',
    [QueueElementType.REJECT]: 'Task reject',
    [QueueElementType.CONFIRM_REJECT]: 'Task reject confirmation',
    [QueueElementType.CREATE]: 'Task create',
    [QueueElementType.MARK_NOTIFICATION_READ]: 'Notification mark as read',
  };

  constructor(
    public readonly userId: Nullable<string>,
    public readonly deviceId: string,
    public readonly apiId: string,
    public readonly trigger: PresenceLogoutTrigger,
    private readonly lostElements: QueueElement[]
  ) {}

  summary(): string {
    const lines = this.lostElements.map((element) => this.describe(element));
    const summary = [this.header(), ...lines].join('\n');
    return summary;
  }

  private header(): string {
    const logoutDescription = LostChangesReport.LOGOUT_DESCRIPTION_BY_TRIGGER[this.trigger];
    const header = `${this.lostElements.length} pending change(s) lost because of the presence control auto-logout behavior (${logoutDescription}):`;
    return header;
  }

  private describe(element: QueueElement): string {
    const operation = LostChangesReport.OPERATION_BY_TYPE[element.type];
    const errors = this.describeErrors(element);
    const description = `- ${operation} ${element.publicId} queued at ${element.createdAt.atom}: ${errors}`;
    return description;
  }

  // A connection failure leaves no error on the element, so an element without errors never reached the server.
  private describeErrors(element: QueueElement): string {
    if (element.errors.length === 0) {
      return 'no error recorded';
    }

    const reasons = element.errors.map((error) => error.reason).join(', ');
    return reasons;
  }
}
